import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Film, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [conf, setConf] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== conf) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset! Please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="reset-brand">
          <div className="w-8 h-8 rounded-md bg-[#00E5FF] flex items-center justify-center glow-cyan">
            <Film className="w-5 h-5 text-black" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">VIRAL3D</span>
        </Link>

        {!token ? (
          <div data-testid="reset-invalid">
            <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Invalid link</h1>
            <p className="text-white/50 font-light mb-8 text-sm">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-[#00E5FF] hover:underline text-sm">Request a new link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/30 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-6 h-6 text-[#39FF14]" />
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Password reset</h1>
            <p className="text-white/50 font-light text-sm">Taking you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Set a new password</h1>
            <p className="text-white/50 font-light mb-8 text-sm">Choose a strong password you don't use elsewhere.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-white/70 text-sm">New password</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="new-password"
                  className="mt-1.5 bg-zinc-950 border-white/15 h-11 focus-visible:ring-[#00E5FF]"
                  placeholder="••••••••" data-testid="reset-new-password" />
              </div>
              <div>
                <Label className="text-white/70 text-sm">Confirm new password</Label>
                <Input type="password" value={conf} onChange={(e) => setConf(e.target.value)} required autoComplete="new-password"
                  className="mt-1.5 bg-zinc-950 border-white/15 h-11 focus-visible:ring-[#00E5FF]"
                  placeholder="••••••••" data-testid="reset-confirm-password" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium"
                data-testid="reset-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
